import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, clearToken, getToken, setToken } from '../lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch (_e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip /me check if returning from Google OAuth (AuthCallback will handle it)
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const loginWithPhone = useCallback(async (phone, password) => {
    const { data } = await api.post('/auth/login', { phone, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    let referrer = null;
    try { referrer = localStorage.getItem('aura.ref') || null; } catch { /* SSR */ }
    const body = referrer ? { ...payload, referrer } : payload;
    const { data } = await api.post('/auth/register', body);
    try { if (referrer) localStorage.removeItem('aura.ref'); } catch { /* noop */ }
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const { data } = await api.post('/auth/session', { session_id: sessionId });
    // session cookie is set by backend; also stash token for header fallback
    if (data.token) setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (_e) { /* ignore network errors on logout */ }
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user, loading, refresh, loginWithPhone, register, exchangeGoogleSession, logout,
    setUser,
    isAuthenticated: !!user,
    isAdmin: !!user && (user.roles || []).includes('admin'),
  }), [user, loading, refresh, loginWithPhone, register, exchangeGoogleSession, logout]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
